import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Divider,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Typography,
  message,
} from "antd";
import { PlusOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../../services/api/apiClient";
import {
  genericVarietyName,
  matchSpecies,
  type SpeciesLike,
} from "../../../utils/speciesMatch";

const { Text } = Typography;

export interface VarietyOption {
  id: string;
  name: string;
  species_id: string | null;
  species_name: string | null;
}

interface SpeciesOption {
  value: string;
  label: string;
}

interface Props {
  value?: string | null;
  onChange?: (
    id: string | null,
    variety: VarietyOption | null,
  ) => void;
  /**
   * Catálogo completo de especies (formato `useSelect`, con `value`+`label`).
   * Se usa tanto para el matching automático como para el select de la
   * modal de creación.
   */
  speciesOptions: SpeciesOption[];
  disabled?: boolean;
  placeholder?: string;
}

const SEARCH_DEBOUNCE_MS = 250;
const MIN_SEARCH_CHARS = 2;

/**
 * Buscador de variedad para el flujo de creación de cultivo colindante.
 *
 * Reemplaza al par "Especie → Variedad" del diseño anterior: el auditor
 * teclea directamente el nombre de la variedad y el picker:
 *
 *   1. Busca en `/crop-varieties/` en el servidor (search por name).
 *   2. Si el string tecleado casa con un nombre de especie (via
 *      `matchSpecies` — misma heurística que usa el móvil), sugiere
 *      crear una "Genérica de especie X" enlazada al species_id.
 *   3. Si no hay match ni resultados, el auditor abre la modal, elige
 *      especie a mano y creamos la variedad literal.
 *
 * El componente emite el `variety_id` seleccionado además del objeto
 * completo (con species_id/species_name) para que el formulario padre
 * pueda auto-rellenar el campo "species" sin pedirlo dos veces.
 */
export const AdjacentCropVarietyPicker = ({
  value,
  onChange,
  speciesOptions,
  disabled,
  placeholder,
}: Props) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [options, setOptions] = useState<VarietyOption[]>([]);
  // Necesitamos guardar la variedad seleccionada aparte del listado
  // remoto — cuando el usuario haya buscado otra cosa, el `value` sigue
  // apuntando a la anterior y AntD necesita la label a partir de las
  // options que le pasamos. `selectedOption` sirve de fallback.
  const [selectedOption, setSelectedOption] = useState<VarietyOption | null>(
    null,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [modalName, setModalName] = useState("");
  const [modalSpecies, setModalSpecies] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Especies creadas inline desde este picker (no vienen en el prop
  // `speciesOptions` porque el useSelect del padre no se refresca sin
  // remount). Las guardamos aparte y las mergeamos con las del prop
  // para que el auditor las vea inmediatamente en el dropdown.
  const [inlineCreatedSpecies, setInlineCreatedSpecies] = useState<
    SpeciesOption[]
  >([]);
  // Estado del sub-flujo "crear especie" dentro del propio modal.
  const [speciesSearch, setSpeciesSearch] = useState("");
  const [speciesModalOpen, setSpeciesModalOpen] = useState(false);
  const [speciesModalName, setSpeciesModalName] = useState("");
  const [creatingSpecies, setCreatingSpecies] = useState(false);

  // Lista efectiva de especies = las del prop + las creadas inline
  // desde este picker. De-dup por id (el prop puede refrescarse en
  // algún ciclo y traer las creadas inline; nos quedamos con la
  // primera aparición).
  const effectiveSpeciesOptions: SpeciesOption[] = useMemo(() => {
    const map = new Map<string, SpeciesOption>();
    for (const s of speciesOptions) map.set(s.value, s);
    for (const s of inlineCreatedSpecies) {
      if (!map.has(s.value)) map.set(s.value, s);
    }
    return Array.from(map.values()).sort((a, b) =>
      String(a.label).localeCompare(String(b.label)),
    );
  }, [speciesOptions, inlineCreatedSpecies]);

  const speciesLike: SpeciesLike[] = useMemo(
    () => effectiveSpeciesOptions.map((s) => ({ id: s.value, name: s.label })),
    [effectiveSpeciesOptions],
  );

  // Cuando el `value` cambia por fuera (p.ej. al inicializar el form,
  // limpiar tras submit, o auto-select tras creación) resincronizamos
  // `selectedOption`. Si no lo encontramos entre options + selectedOption
  // consultamos al backend por el detalle.
  useEffect(() => {
    if (value == null) {
      setSelectedOption(null);
      return;
    }
    const fromList = options.find((o) => o.id === value);
    if (fromList) {
      setSelectedOption(fromList);
      return;
    }
    if (selectedOption?.id === value) return;
    // Fetch puntual por id — evita quedarnos sin label si el value se
    // establece antes de la primera búsqueda.
    let alive = true;
    (async () => {
      try {
        const resp = await apiClient.get(`/crop-varieties/${value}/`);
        if (!alive) return;
        const v = resp?.data;
        if (v?.id != null) {
          setSelectedOption({
            id: v.id,
            name: v.name,
            species_id: v.species ?? null,
            species_name: v.species_name ?? null,
          });
        }
      } catch {
        // silenciamos: el select mostrará el id crudo hasta que el
        // usuario vuelva a buscar.
      }
    })();
    return () => {
      alive = false;
    };
  }, [value, options, selectedOption?.id]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = search.trim();
    if (q.length < MIN_SEARCH_CHARS) {
      setOptions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        // El CropVarietyViewSet tiene `search_fields = ['name',
        // 'species__name']` y responde con `{ data: [...], total }`
        // vía RefineCompatiblePagination — usa `page_size` (snake).
        const resp = await apiClient.get("/crop-varieties/", {
          params: { search: q, page_size: 25, is_active: true },
        });
        const payload = resp?.data;
        const rows: any[] = Array.isArray(payload)
          ? payload
          : (payload?.data ?? payload?.results ?? []);
        setOptions(
          rows.map((v) => ({
            id: v.id,
            name: v.name,
            species_id: v.species ?? null,
            species_name: v.species_name ?? null,
          })),
        );
      } catch {
        setOptions([]);
      } finally {
        setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  const trimmedSearch = search.trim();
  const detectedSpecies = useMemo(() => {
    if (!trimmedSearch) return null;
    return matchSpecies(trimmedSearch, speciesLike);
  }, [trimmedSearch, speciesLike]);
  const isCreatable =
    !disabled &&
    trimmedSearch.length >= MIN_SEARCH_CHARS &&
    !options.some(
      (o) => o.name.trim().toLowerCase() === trimmedSearch.toLowerCase(),
    );

  const openCreateModal = () => {
    if (!isCreatable) return;
    // Si detectamos que el nombre tecleado casa con una especie, la
    // variedad se propone como "Genérica de <especie>" — el nombre por
    // defecto de la modal ya lo refleja para que el auditor lo vea antes
    // de confirmar.
    if (detectedSpecies) {
      setModalName(genericVarietyName(detectedSpecies.name));
      setModalSpecies(String(detectedSpecies.id));
    } else {
      setModalName(trimmedSearch);
      setModalSpecies(null);
    }
    setModalOpen(true);
  };

  // Crea especie inline desde el sub-modal — se llama cuando el
  // auditor teclea un nombre que no existe en el select de especies
  // dentro del modal de variedad y pulsa "Crear especie X". Al éxito,
  // añade la especie a `inlineCreatedSpecies` (que hace merge con
  // `speciesOptions`) y la deja seleccionada como `modalSpecies` para
  // que continúe con la creación de la variedad sin fricción.
  const submitCreateSpecies = async () => {
    const name = speciesModalName.trim();
    if (!name) {
      message.warning(
        t(
          "adjacent_crops.species_picker.name_required",
          "El nombre de la especie es obligatorio",
        ),
      );
      return;
    }
    setCreatingSpecies(true);
    try {
      const resp = await apiClient.post("/crop-species/", { name });
      const s = resp?.data;
      const opt: SpeciesOption = {
        value: String(s.id),
        label: s.name || name,
      };
      setInlineCreatedSpecies((prev) => {
        if (prev.some((x) => x.value === opt.value)) return prev;
        return [...prev, opt];
      });
      setModalSpecies(opt.value);
      setSpeciesModalOpen(false);
      setSpeciesSearch("");
      message.success(
        t(
          "adjacent_crops.species_picker.created",
          "Especie «{{name}}» creada",
          { name: opt.label },
        ),
      );
    } catch (err: any) {
      const data = err?.response?.data;
      const detail =
        data?.name?.[0] || data?.detail || err?.message;
      message.error(
        detail
          || t(
            "adjacent_crops.species_picker.create_error",
            "No se pudo crear la especie",
          ),
      );
    } finally {
      setCreatingSpecies(false);
    }
  };

  const openCreateSpeciesModal = () => {
    setSpeciesModalName(speciesSearch.trim());
    setSpeciesModalOpen(true);
  };

  const submitCreate = async () => {
    const name = modalName.trim();
    if (!name) {
      message.warning(
        t(
          "adjacent_crops.variety_picker.name_required",
          "El nombre es obligatorio",
        ),
      );
      return;
    }
    if (!modalSpecies) {
      message.warning(
        t(
          "adjacent_crops.variety_picker.species_required",
          "Elige a qué especie pertenece la variedad",
        ),
      );
      return;
    }
    setCreating(true);
    try {
      const resp = await apiClient.post("/crop-varieties/", {
        name,
        species: modalSpecies,
        is_active: true,
      });
      const v = resp?.data;
      const opt: VarietyOption = {
        id: v.id,
        name: v.name,
        species_id: v.species ?? modalSpecies,
        species_name:
          v.species_name ??
          speciesOptions.find((s) => s.value === modalSpecies)?.label ??
          null,
      };
      // Metemos la nueva variedad en `options` para que el Select la
      // pueda pintar sin refrescar la búsqueda.
      setOptions((prev) => {
        if (prev.some((o) => o.id === opt.id)) return prev;
        return [opt, ...prev];
      });
      setSelectedOption(opt);
      onChange?.(opt.id, opt);
      setModalOpen(false);
      setSearch("");
      message.success(
        t(
          "adjacent_crops.variety_picker.created",
          "Variedad «{{name}}» creada",
          { name: opt.name },
        ),
      );
    } catch (err: any) {
      // El backend valida `unique_together (species, name)` — mostramos
      // el mensaje textual si viene y si no un genérico.
      const data = err?.response?.data;
      const detail =
        data?.name?.[0] ||
        data?.species?.[0] ||
        data?.detail ||
        err?.message;
      message.error(
        detail ||
          t(
            "adjacent_crops.variety_picker.create_error",
            "No se pudo crear la variedad",
          ),
      );
    } finally {
      setCreating(false);
    }
  };

  const selectOptions = useMemo(() => {
    const map = new Map<string, VarietyOption>();
    for (const o of options) map.set(o.id, o);
    if (selectedOption && !map.has(selectedOption.id)) {
      map.set(selectedOption.id, selectedOption);
    }
    return Array.from(map.values()).map((v) => ({
      value: v.id,
      label: (
        <span>
          <b>{v.name}</b>
          {v.species_name ? (
            <Text type="secondary"> — {v.species_name}</Text>
          ) : null}
        </span>
      ),
      _variety: v,
    }));
  }, [options, selectedOption]);

  return (
    <>
      <Select<string>
        value={value ?? undefined}
        disabled={disabled}
        allowClear
        showSearch
        filterOption={false}
        placeholder={
          placeholder ??
          t(
            "adjacent_crops.variety_picker.placeholder",
            "Busca la variedad o teclea un nombre nuevo…",
          )
        }
        onSearch={setSearch}
        onChange={(id, opt) => {
          if (id == null) {
            setSelectedOption(null);
            onChange?.(null, null);
            return;
          }
          const found: VarietyOption | null =
            ((opt as any)?._variety as VarietyOption | undefined) ??
            options.find((o) => o.id === id) ??
            selectedOption ??
            null;
          setSelectedOption(found);
          onChange?.(id, found);
        }}
        onBlur={() => setSearch("")}
        notFoundContent={
          searching ? (
            <div style={{ textAlign: "center", padding: 8 }}>
              <Spin size="small" />
            </div>
          ) : trimmedSearch.length < MIN_SEARCH_CHARS ? (
            <Text type="secondary">
              {t(
                "adjacent_crops.variety_picker.hint_min",
                "Escribe al menos {{n}} letras",
                { n: MIN_SEARCH_CHARS },
              )}
            </Text>
          ) : (
            <Text type="secondary">
              {t(
                "adjacent_crops.variety_picker.no_matches",
                "Sin coincidencias",
              )}
            </Text>
          )
        }
        options={selectOptions as any}
        dropdownRender={(menu) => (
          <>
            {menu}
            {isCreatable && (
              <>
                <Divider style={{ margin: "6px 0" }} />
                <div style={{ padding: "6px 8px" }}>
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={openCreateModal}
                    style={{ paddingLeft: 0 }}
                  >
                    {detectedSpecies
                      ? t(
                          "adjacent_crops.variety_picker.create_generic",
                          "Crear «Genérica de especie {{species}}»",
                          { species: detectedSpecies.name },
                        )
                      : t(
                          "adjacent_crops.variety_picker.create_named",
                          "Crear variedad «{{name}}»",
                          { name: trimmedSearch },
                        )}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      />

      <Modal
        title={t(
          "adjacent_crops.variety_picker.modal_title",
          "Crear variedad nueva",
        )}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submitCreate}
        confirmLoading={creating}
        okText={t("common.create", "Crear")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnClose
        width={480}
      >
        <Form layout="vertical">
          <Form.Item
            label={t(
              "adjacent_crops.variety_picker.name_label",
              "Nombre de la variedad",
            )}
            required
          >
            <Input
              value={modalName}
              onChange={(e) => setModalName(e.target.value)}
              autoFocus
              placeholder={t(
                "adjacent_crops.variety_picker.name_ph",
                "Ej: «Nadorcott», «Genérica de especie Aguacate»",
              )}
              maxLength={100}
            />
          </Form.Item>
          <Form.Item
            label={t("adjacent_crops.field.species", "Especie")}
            required
            extra={
              detectedSpecies ? (
                <Text type="secondary">
                  {t(
                    "adjacent_crops.variety_picker.detected_species_hint",
                    "Detectada automáticamente a partir del nombre.",
                  )}
                </Text>
              ) : (
                <Text type="secondary">
                  {t(
                    "adjacent_crops.species_picker.extra",
                    "Si la especie no existe, teclea su nombre y pulsa «Crear especie».",
                  )}
                </Text>
              )
            }
          >
            <Select<string>
              value={modalSpecies ?? undefined}
              onChange={(val) => setModalSpecies(val ?? null)}
              onSearch={setSpeciesSearch}
              onBlur={() => setSpeciesSearch("")}
              options={effectiveSpeciesOptions}
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              placeholder={t(
                "adjacent_crops.placeholder.species",
                "Seleccione una especie",
              )}
              allowClear
              dropdownRender={(menu) => {
                const q = speciesSearch.trim();
                const alreadyExists = effectiveSpeciesOptions.some(
                  (o) =>
                    String(o.label).trim().toLowerCase() === q.toLowerCase(),
                );
                const showCreate = q.length >= 2 && !alreadyExists;
                return (
                  <>
                    {menu}
                    {showCreate && (
                      <>
                        <Divider style={{ margin: "6px 0" }} />
                        <div style={{ padding: "6px 8px" }}>
                          <Button
                            type="link"
                            icon={<PlusCircleOutlined />}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={openCreateSpeciesModal}
                            style={{ paddingLeft: 0 }}
                          >
                            {t(
                              "adjacent_crops.species_picker.create_named",
                              "Crear especie «{{name}}»",
                              { name: q },
                            )}
                          </Button>
                        </div>
                      </>
                    )}
                  </>
                );
              }}
            />
          </Form.Item>
          {detectedSpecies && (
            <Alert
              type="info"
              showIcon
              message={
                <Space direction="vertical" size={0}>
                  <span>
                    {t(
                      "adjacent_crops.variety_picker.generic_hint",
                      "Se guardará como variedad genérica de la especie detectada — el admin podrá renombrarla más tarde si aparece un nombre comercial concreto.",
                    )}
                  </span>
                </Space>
              }
            />
          )}
        </Form>
      </Modal>

      {/* Sub-modal — crear especie inline. Se abre desde el
          dropdownRender del select de especies dentro del modal de
          variedad. Deliberadamente pequeño y sin campos extra
          (`scientific_name` queda como null; el admin lo puede
          completar más tarde desde /admin/crops). */}
      <Modal
        title={t(
          "adjacent_crops.species_picker.modal_title",
          "Crear especie nueva",
        )}
        open={speciesModalOpen}
        onCancel={() => setSpeciesModalOpen(false)}
        onOk={submitCreateSpecies}
        confirmLoading={creatingSpecies}
        okText={t("common.create", "Crear")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnClose
        width={420}
        zIndex={1100}
      >
        <Form layout="vertical">
          <Form.Item
            label={t(
              "adjacent_crops.species_picker.name_label",
              "Nombre de la especie",
            )}
            required
            extra={
              <Text type="secondary">
                {t(
                  "adjacent_crops.species_picker.name_extra",
                  "Ej: «Aguacate», «Mandarino». Los detalles adicionales (nombre científico) los puede completar el admin en /admin/crops.",
                )}
              </Text>
            }
          >
            <Input
              value={speciesModalName}
              onChange={(e) => setSpeciesModalName(e.target.value)}
              autoFocus
              placeholder={t(
                "adjacent_crops.species_picker.name_ph",
                "Ej: Aguacate",
              )}
              maxLength={100}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AdjacentCropVarietyPicker;
